import React from 'react'
import { Link } from 'react-router-dom'
import { UsersIcon, XIcon } from 'lucide-react'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

export type DayAssignCrew = { id: string; name: string; is_subcontractor?: boolean | null }
export type DayAssignEmployee = {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
  role?: string
}
export type DayAssignRow = {
  id: string
  crew_id?: string | null
  employee_id?: string | null
  crew?: DayAssignCrew | null
  employee?: DayAssignEmployee | null
}

type Props = {
  assignments: DayAssignRow[]
  crews: DayAssignCrew[]
  employees: DayAssignEmployee[]
  isEditing: boolean
  saving: boolean
  /** Optional day workload counts keyed by crew/employee id */
  crewJobCounts?: Record<string, number>
  employeeJobCounts?: Record<string, number>
  onToggleEdit: () => void
  onAssignCrew: (crewId: string) => void
  onAssignEmployee: (employeeId: string) => void
  onRemove: (assignmentId: string) => void
  compact?: boolean
}

function assigneeLabel(a: DayAssignRow): string {
  if (a.crew) return a.crew.name
  if (a.employee) return formatEmployeeLabel(a.employee)
  return '—'
}

function workloadHint(count: number | undefined): string {
  if (count == null) return ''
  if (count === 0) return ' · free'
  return ` · ${count} today`
}

function SubcontractorBadge() {
  return (
    <span className="inline-flex px-1 py-0.5 text-[9px] leading-none rounded bg-orange-100 text-orange-800">
      Sub
    </span>
  )
}

export function DayJobAssignPanel({
  assignments,
  crews,
  employees,
  isEditing,
  saving,
  crewJobCounts,
  employeeJobCounts,
  onToggleEdit,
  onAssignCrew,
  onAssignEmployee,
  onRemove,
  compact = false,
}: Props) {
  const availableCrews = crews
    .filter((c) => !assignments.some((a) => a.crew_id === c.id))
    .slice()
    .sort((a, b) => (crewJobCounts?.[a.id] ?? 0) - (crewJobCounts?.[b.id] ?? 0))

  const availableEmployees = employees
    .filter((emp) => !assignments.some((a) => a.employee_id === emp.id))
    .slice()
    .sort(
      (a, b) => (employeeJobCounts?.[a.id] ?? 0) - (employeeJobCounts?.[b.id] ?? 0)
    )

  return (
    <div className={compact ? '' : 'pt-2 border-t border-gray-100'}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
          <UsersIcon className="w-3 h-3" /> Assigned
        </span>
        <button
          type="button"
          onClick={onToggleEdit}
          className="text-[11px] text-blue-600 hover:text-blue-800 font-medium"
        >
          {isEditing ? 'Done' : assignments.length > 0 ? 'Edit' : '+ Assign'}
        </button>
      </div>

      {assignments.length > 0 && !isEditing && (
        <div className="flex flex-wrap gap-1">
          {assignments.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 font-medium"
            >
              {assigneeLabel(a)}
              {a.crew?.is_subcontractor && <SubcontractorBadge />}
            </span>
          ))}
        </div>
      )}

      {assignments.length === 0 && !isEditing && (
        <p className="text-xs text-amber-700 italic">Unassigned</p>
      )}

      {isEditing && (
        <div className="space-y-2 mt-1">
          {assignments.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {assignments.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 font-medium"
                >
                  {assigneeLabel(a)}
                  {a.crew?.is_subcontractor && <SubcontractorBadge />}
                  <button
                    type="button"
                    onClick={() => onRemove(a.id)}
                    disabled={saving}
                    className="hover:text-red-600 transition-colors disabled:opacity-50"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {crews.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) onAssignCrew(e.target.value)
                e.target.value = ''
              }}
              disabled={saving}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">+ Add crew...</option>
              {availableCrews.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.is_subcontractor ? ' (Sub)' : ''}
                  {workloadHint(crewJobCounts?.[c.id])}
                </option>
              ))}
            </select>
          )}
          {employees.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) onAssignEmployee(e.target.value)
                e.target.value = ''
              }}
              disabled={saving}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="">+ Add employee...</option>
              {availableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {formatEmployeeLabel(emp)}
                  {emp.role ? ` (${emp.role})` : ''}
                  {workloadHint(employeeJobCounts?.[emp.id])}
                </option>
              ))}
            </select>
          )}
          {crews.length === 0 && employees.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              No crews or employees found.{' '}
              <Link to="/settings/team" className="text-blue-600 underline">
                Add them in Team
              </Link>
              .
            </p>
          )}
        </div>
      )}
    </div>
  )
}
